import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, DoorClosed, ClipboardCheck, Ruler, Plus, FilePlus, ShieldCheck, QrCode } from "lucide-react";
import { doorsApi, inspectionsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { VerifyRepairDialog } from "@/components/VerifyRepairDialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function Field({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-700">{value}</p>
    </div>
  );
}

export default function DoorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const door = useAsync(() => doorsApi.getOne(id), [id]);
  const inspections = useAsync(() => inspectionsApi.getAll({ door_id: id }), [id]);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const d = door.data;
  const list = Array.isArray(inspections.data) ? inspections.data : [];

  const dims = d
    ? [d.door_height_mm, d.door_width_mm, d.door_thickness_mm].some(Boolean)
      ? `${d.door_height_mm || "—"} × ${d.door_width_mm || "—"} × ${d.door_thickness_mm || "—"} mm`
      : null
    : null;

  return (
    <div>
      {d?.building_id ? (
        <Link to={`/buildings/${d.building_id}`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back to building
        </Link>
      ) : (
        <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      )}

      {door.loading && <LoadingState label="Loading door…" />}
      {!door.loading && door.error && <ErrorState message={door.error} onRetry={door.reload} />}

      {!door.loading && !door.error && d && (
        <>
          <PageHeader
            title={d.door_reference || "Door"}
            subtitle={d.location}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {d.last_inspection_status ? (
                  <Badge variant="outline" className={statusTone(d.last_inspection_status)}>
                    Last: {prettyStatus(d.last_inspection_status)}
                  </Badge>
                ) : null}
                <Button onClick={() => navigate(`/inspections/new?doorId=${id}&buildingId=${d.building_id}`)} data-testid="new-inspection-btn">
                  <Plus className="mr-1.5 h-4 w-4" /> New inspection
                </Button>
                <Button variant="outline" onClick={() => navigate(`/quotes/new?mode=door&building=${d.building_id}&door=${id}`)} data-testid="door-create-quote">
                  <FilePlus className="mr-1.5 h-4 w-4" /> Create quote
                </Button>
                <Button variant="outline" onClick={() => navigate(`/doors/${id}/label`)} data-testid="door-qr-label">
                  <QrCode className="mr-1.5 h-4 w-4" /> QR label
                </Button>
              </div>
            }
            testId="door-detail-header"
          />

          {d.pending_reinspection && (
            <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 border-amber-200 bg-amber-50 p-4" data-testid="door-pending-reinspection">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <ShieldCheck className="h-5 w-5 text-amber-600" />
                <span>Remedial works complete — this door is awaiting post-work verification.</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setVerifyOpen(true)} data-testid="door-verify-repair-btn">
                  <ShieldCheck className="mr-1.5 h-4 w-4" /> Verify repair
                </Button>
                <Button size="sm" onClick={() => navigate(`/inspections/new?doorId=${id}&buildingId=${d.building_id}`)} data-testid="door-reinspect-btn">
                  <Plus className="mr-1.5 h-4 w-4" /> Re-inspect
                </Button>
              </div>
            </Card>
          )}

          <VerifyRepairDialog
            doorId={id}
            doorRef={d.door_reference}
            open={verifyOpen}
            onOpenChange={setVerifyOpen}
            onDone={() => { door.reload(); inspections.reload(); }}
          />

          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2">
              <div className="mb-4 flex items-center gap-2 text-slate-800">
                <DoorClosed className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Door details</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <Field label="Type" value={<span className="capitalize">{d.door_type}</span>} />
                <Field label="Leaf" value={<span className="capitalize">{d.door_leaf_type}</span>} />
                <Field label="FD rating" value={d.fd_rating} />
                <Field label="Floor" value={d.floor} />
                <Field label="Apartment" value={d.apartment_number} />
                <Field label="Usage" value={d.usage_type} />
                <Field label="Frequency" value={(d.inspection_frequency || "").replace("_", " ")} />
                <Field label="Style" value={d.door_style} />
                <Field label="Dimensions" value={dims} />
                <Field label="Last inspection" value={fmtDate(d.last_inspection_date)} />
                <Field label="Next due" value={fmtDate(d.next_inspection_date)} />
              </div>
            </Card>
            {d.door_photo && (
              <Card className="overflow-hidden">
                <img src={d.door_photo} alt="Door" className="h-full max-h-72 w-full object-cover" />
              </Card>
            )}
          </div>

          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <ClipboardCheck className="h-5 w-5 text-slate-400" /> Inspection history ({list.length})
          </h2>

          {inspections.loading && <LoadingState label="Loading inspections…" rows={2} />}
          {!inspections.loading && inspections.error && <ErrorState message={inspections.error} onRetry={inspections.reload} />}
          {!inspections.loading && !inspections.error && list.length === 0 && (
            <EmptyState title="No inspections yet" message="This door has not been inspected." testId="door-inspections-empty" />
          )}

          {!inspections.loading && !inspections.error && list.length > 0 && (
            <div className="space-y-2" data-testid="door-inspections-list">
              {list.map((insp) => (
                <Card
                  key={insp.id}
                  className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md"
                  onClick={() => navigate(`/inspections/${insp.id}`)}
                  data-testid={`inspection-row-${insp.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{fmtDate(insp.inspection_date)}</p>
                    <p className="truncate text-sm text-slate-500">Inspector: {insp.inspector_name || "—"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {insp.has_quote && <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Quoted</Badge>}
                    <Badge variant="outline" className={statusTone(insp.status)}>{prettyStatus(insp.status)}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
